import React, { useEffect, useState } from "react";
import RowsPerPageSelect from "components/admin/RowsPerPageSelect";
import AdminModal from "components/admin/AdminModal";
import UserFilterDropdown from "./userFilterDropdown";
import MoveToModal from "./moveToModal";
import UserDetailsModal from "./userDetailsModal";
import { moveUsersToOgGroup } from "./ogGroupActions";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import { OgUserGroup, User } from "./type";
import { getUsers, getUsersByRole } from "apis/adminServices/userManagmentService";
import { hasLifetimeOgRole } from "utils/roles";

interface OgGroupUsersProps {
  /** The OG user group this tab lists — one of GetOgUserGroups' rows. */
  group: OgUserGroup;
  /** Every group, for the "Move to" targets. */
  groups: OgUserGroup[];
  /** Reloads the roles and the tab counts after users change group. */
  onMembershipChange?: () => void;
}

/**
 * The member list of one OG user group. Every role tab renders this same
 * component — the group it lists is a prop, so the roles an admin configures
 * through User Role Configuration each get a working tab with no code change.
 *
 * The parent keys this on `group.id`, so switching tabs remounts it and no
 * search, page or selection leaks from one group into the next.
 */
const OgGroupUsers: React.FC<OgGroupUsersProps> = ({
  group,
  groups,
  onMembershipChange,
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // GetUsers is scoped to this group by `roleId`, so its `totalCount` is the
  // group's own size — both the record count and what the pager pages over.
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isMoveToOpen, setIsMoveToOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [detailsUser, setDetailsUser] = useState<User | null>(null);
  const [userToRemove, setUserToRemove] = useState<User | null>(null);
  const [removing, setRemoving] = useState(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const { showToast } = useToastStore();

  const fetchUsers = async (
    page: number,
    size: number,
    showGlobalLoader = true
  ) => {
    try {
      setLoading(true);
      if (showGlobalLoader) showLoader();
      const response = await getUsers(searchTerm, filter, page, size, group.id);
      const total = response?.totalCount ?? 0;
      setUsers(getUsersByRole(response, group.roleName));
      setTotalUsers(total);

      // Moving or removing the last rows of the last page leaves the pager
      // pointing past the end, and the tab reads as "no users" while the group
      // still has some. Jump to where the rows actually are, in one hop.
      const lastPage = Math.max(1, Math.ceil(total / size));
      if (page > lastPage) setCurrentPage(lastPage);
    } catch (error) {
      console.error(`Error fetching ${group.roleName} users:`, error);
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    const isSearch = searchTerm.trim().length > 0;
    fetchUsers(currentPage, pageSize, !isSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, searchTerm, filter, group.id]);

  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  const generatePageNumbers = () => {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setSelectedUsers([]);
  };

  const handleFilterChange = (filters: { status?: string }) => {
    setFilter(filters.status || "");
    setCurrentPage(1);
    setSelectedUsers([]);
    setIsFilterOpen(false);
  };

  const toggleUserSelection = (user: User) => {
    setSelectedUsers((prev) =>
      prev.some((u) => u.userId === user.userId)
        ? prev.filter((u) => u.userId !== user.userId)
        : [...prev, user]
    );
  };

  const allOnPageSelected =
    users.length > 0 &&
    users.every((user) =>
      selectedUsers.some((selected) => selected.userId === user.userId)
    );

  const togglePageSelection = () => {
    if (allOnPageSelected) {
      setSelectedUsers((prev) =>
        prev.filter((selected) => !users.some((user) => user.userId === selected.userId))
      );
    } else {
      setSelectedUsers((prev) => [
        ...prev,
        ...users.filter(
          (user) => !prev.some((selected) => selected.userId === user.userId)
        ),
      ]);
    }
  };

  const handleMoveToClick = () => {
    if (selectedUsers.length === 0) {
      showToast("Please select at least one user to move.", "error", 3000);
      return;
    }
    const blockedUsers = selectedUsers.filter(
      (u) => u.status?.toLowerCase() === "blocked"
    );
    if (blockedUsers.length > 0) {
      if (blockedUsers.length === 1 && selectedUsers.length === 1) {
        showToast(
          "Blocked users cannot be moved to another user role.",
          "error",
          3000
        );
      } else {
        showToast(
          "Blocked users cannot be moved to another user role. Please deselect blocked user(s) to continue.",
          "error",
          3000
        );
      }
      return;
    }
    setIsMoveToOpen(true);
  };

  const handleMoveTo = async (roleId: number | null) => {
    const blockedUsers = selectedUsers.filter(
      (u) => u.status?.toLowerCase() === "blocked"
    );
    if (blockedUsers.length > 0) {
      showToast(
        "Blocked users cannot be moved to another user role.",
        "error",
        3000
      );
      return;
    }
    setMoving(true);
    const result = await moveUsersToOgGroup(selectedUsers, roleId, groups);
    setMoving(false);
    showToast(
      result.message,
      result.toastType || (result.failed > 0 ? "error" : "success"),
      3000
    );

    setIsMoveToOpen(false);
    setSelectedUsers([]);
    if (result.succeeded > 0) {
      // Moved users drop out of this list, so reload it.
      fetchUsers(currentPage, pageSize, false);
      onMembershipChange?.();
    }
  };

  /**
   * There is no RemoveFromOgGroup endpoint — leaving the group is the same
   * write as moving to "General Users", so this is MoveUserToOgGroup with a
   * null roleId, exactly what the Move To modal sends for that option.
   */
  const confirmRemove = async () => {
    if (!userToRemove) return;
    setRemoving(true);
    const result = await moveUsersToOgGroup([userToRemove], null, groups);
    setRemoving(false);
    showToast(
      result.message,
      result.toastType || (result.failed > 0 ? "error" : "success"),
      3000
    );

    if (result.succeeded > 0) {
      setUserToRemove(null);
      // The user is no longer in this group, so drop them from the selection.
      setSelectedUsers((prev) =>
        prev.filter((selected) => selected.userId !== userToRemove.userId)
      );
      fetchUsers(currentPage, pageSize, false);
      onMembershipChange?.();
    }
  };

  return (
    <section>
      <div className="bg-white rounded-xl p-4 mt-2">
        <div className="flex flex-wrap gap-4 items-center justify-end pb-4">
          <div className="flex flex-wrap items-center gap-3 relative">
            <div className="relative">
              <input
                className="block w-full p-3 pl-10 pr-10 rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                placeholder="Search by keyword"
                value={searchTerm}
                onChange={handleSearch}
              />
              <button className="absolute w-fit inset-y-0 start-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none">
                <i className="st-search text-lightgray-200"></i>
              </button>
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="absolute inset-y-0 end-0 flex items-center z-30 px-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  <svg className="w-3 h-3" viewBox="0 0 14 14" fill="none">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                    />
                  </svg>
                </button>
              )}
            </div>

            <button
              className="flex items-center justify-center gap-2 p-3 rounded-lg bg-[#FFBC00] hover:opacity-90 transition-opacity"
              onClick={handleMoveToClick}
            >
              <p className="font-medium text-white text-sm">Move to</p>
            </button>

            <div className="relative">
              <button
                className="flex items-center justify-center gap-2 p-3 bg-[#FFBC00] hover:opacity-90 transition-opacity rounded-lg"
                onClick={() => setIsFilterOpen(true)}
              >
                <i className="st-filter text-white"></i>
                <p className="font-medium text-white text-sm">Filters</p>
              </button>

              <UserFilterDropdown
                onClose={() => setIsFilterOpen(false)}
                onFilterChange={handleFilterChange}
                isOpen={isFilterOpen}
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)] min-h-[300px] sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 bg-blue-50 sticky top-0 z-10 shadow-sm whitespace-nowrap">
              <tr>
                <th scope="col" className="pl-6 pr-2 py-4 rounded-l-lg w-10 sticky top-0 bg-blue-50 z-10 whitespace-nowrap">
                  <input
                    type="checkbox"
                    aria-label="Select all users on this page"
                    checked={allOnPageSelected}
                    disabled={loading || users.length === 0}
                    onChange={togglePageSelection}
                    className="accent-primaryblue-100 cursor-pointer"
                  />
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10 whitespace-nowrap">
                  <p className="text-lightgray-200 whitespace-nowrap">User ID</p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    User Name
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Joined Date
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Email Address
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Status
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 text-center sticky top-0 bg-blue-50 z-10">
                  <p className="border-l-2 border-lightgray-50 text-lightgray-200 px-4">
                    Total Tokens
                  </p>
                </th>
                <th scope="col" className="px-6 py-4 rounded-r-lg sticky top-0 bg-blue-50 z-10"></th>
              </tr>
            </thead>
            <tbody className="rounded-lg">
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-24 text-lightgray-200 text-sm">
                    No users in {group.roleName}.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user?.userId}
                    className="odd:bg-white even:bg-blue-50 border"
                  >
                    <td className="pl-6 pr-2 py-4 w-10">
                      <input
                        type="checkbox"
                        aria-label={`Select user ${user?.userId}`}
                        checked={selectedUsers.some(
                          (selected) => selected.userId === user?.userId
                        )}
                        onChange={() => toggleUserSelection(user)}
                        className="accent-primaryblue-100 cursor-pointer"
                      />
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p>{user?.userId}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {user?.username
                          ? `${user.username.charAt(0).toUpperCase()}${user.username.slice(1)}`
                          : "UserName"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">
                        {user?.joinedDate
                          ? new Date(user.joinedDate).toLocaleDateString("en-GB")
                          : "-"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4">{user?.email}</p>
                    </td>
                    <td className="px-6 py-4 font-normal text-grease-100 whitespace-nowrap">
                      <p
                        className={`mx-4 px-3 py-1 text-white text-xs rounded-full w-fit ${
                          user?.status === "Active" ? "bg-teal-500" : "bg-red-500"
                        }`}
                      >
                        {user?.status || "unknown"}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <p className="px-4 text-center">
                        {hasLifetimeOgRole(user?.userRoles, group.roleName)
                          ? "Unlimited"
                          : user?.totalCredits}
                      </p>
                    </td>
                    <td className="px-6 py-4 font-normal text-sm text-grease-100 whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setDetailsUser(user)}
                          className="rounded-md px-4 py-2 text-xs w-20 text-primaryblue-100 border border-primaryblue-100"
                        >
                          Details
                        </button>
                        <button
                          onClick={() => setUserToRemove(user)}
                          className="rounded-md px-4 py-2 text-xs w-20 text-red-500 border border-red-500"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalUsers > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <div className="flex flex-wrap items-center gap-5">
              <p className="text-lightgray-200 text-sm">
                Total {totalUsers} {totalUsers === 1 ? "record" : "records"}
              </p>
              <p className="text-lightgray-200 text-sm">Rows per page</p>
              <RowsPerPageSelect
                value={pageSize}
                onChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                  setSelectedUsers([]);
                }}
              />
            </div>

            <div>
              <ul className="flex flex-wrap items-center justify-center my-6 gap-3 text-sm">
                <li>
                  <button
                    disabled={currentPage === 1}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === 1
                        ? "text-gray-300 border-gray-300"
                        : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <span className="sr-only">Previous</span>
                    <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                  </button>
                </li>

                {generatePageNumbers().map((pageNum) => (
                  <li key={pageNum}>
                    <button
                      className={`flex items-center justify-center w-7 h-7 rounded-md ${
                        currentPage === pageNum
                          ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                          : "text-primaryblue-50 border border-primaryblue-50"
                      }`}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  </li>
                ))}

                <li>
                  <button
                    disabled={currentPage === totalPages}
                    className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                      currentPage === totalPages
                        ? "text-gray-300 border-gray-300"
                        : "text-primaryblue-50 border-primaryblue-50"
                    }`}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <span className="sr-only">Next</span>
                    <i className="st-arrow-right text-xl font-semibold"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <UserDetailsModal
        isOpen={detailsUser !== null}
        user={detailsUser}
        onClose={() => setDetailsUser(null)}
      />

      <MoveToModal
        isOpen={isMoveToOpen}
        onClose={() => setIsMoveToOpen(false)}
        groups={groups}
        currentRoleId={group.id}
        onMove={handleMoveTo}
        moving={moving}
      />

      <AdminModal
        isOpen={!!userToRemove}
        onClose={() => {
          if (!removing) setUserToRemove(null);
        }}
        title={`Remove from ${group.roleName}`}
        size="sm"
        showCloseButton={false}
      >
        <div className="flex flex-col items-center justify-center p-2 text-center">
          <p className="mb-6 text-gray-600">
            Are you sure you want to remove{" "}
            <span className="font-semibold text-grease-100">
              {userToRemove?.username || `User ${userToRemove?.userId}`}
            </span>{" "}
            from {group.roleName}?
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => setUserToRemove(null)}
              disabled={removing}
              className="px-6 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={confirmRemove}
              disabled={removing}
              className="px-6 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {removing ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Removing...</span>
                </>
              ) : (
                "Remove"
              )}
            </button>
          </div>
        </div>
      </AdminModal>
    </section>
  );
};

export default OgGroupUsers;
